/**
 * Status-effect runtime over a per-entity `StatusBag`.
 *
 * Pure functions only — no HP, no dice, no engine state. The combat engine
 * (HEX-S6) owns the surrounding state machine and routes the damage value
 * returned by `tick` into the bearer's HP; this module is unaware of HP.
 *
 * Overwrite rule (ADR-0010 §2): applying a status with the same `(kind, target)`
 * as an existing entry replaces both `magnitude` and `duration` — no stacking.
 */

import type { StatusApplication, StatusKind } from './types';

/** Ordered collection of active statuses on one bearer. Order is insertion
 * order; replacement preserves position so adjacent equal-kind entries with
 * different targets keep a stable relative order. */
export type StatusBag = readonly StatusApplication[];

/** Tick result: the next bag (durations decremented) plus the integer damage
 * the bearer takes from poison entries this tick. */
export type TickResult = {
  bag: StatusBag;
  ownerDamage: number;
};

export function createStatusBag(): StatusBag {
  return [];
}

export function apply(bag: StatusBag, application: StatusApplication): StatusBag {
  const idx = bag.findIndex(
    (entry) => entry.status === application.status && entry.target === application.target,
  );
  if (idx === -1) {
    return [...bag, application];
  }
  const next = bag.slice();
  next[idx] = application;
  return next;
}

export function tick(bag: StatusBag): TickResult {
  let ownerDamage = 0;
  const next = bag.map((entry) => {
    if (entry.status === 'poison') {
      ownerDamage += entry.magnitude;
    }
    return { ...entry, duration: entry.duration - 1 };
  });
  return { bag: next, ownerDamage };
}

export function expire(bag: StatusBag): StatusBag {
  return bag.filter((entry) => entry.duration > 0);
}

export function query(bag: StatusBag, kind: StatusKind): number {
  let total = 0;
  for (const entry of bag) {
    if (entry.status === kind) total += entry.magnitude;
  }
  return total;
}
